import type { ExerciseDef } from './types'

// ─── 5.2 Conditional probability & Bayes ──────────────────────────────────────

export const condTable: ExerciseDef = {
  starter: `// 200 days of weather, counted:
//                  rain   no rain
//   cloudy           45        35
//   not cloudy        5       115
const cloudyRain = 45, cloudyDry = 35, clearRain = 5, clearDry = 115
const days = cloudyRain + cloudyDry + clearRain + clearDry

console.log("P(rain)          =", (cloudyRain + clearRain) / days)
// "Given cloudy" means: zoom in on the cloudy days only, then count.
console.log("P(rain | cloudy) =", cloudyRain / (cloudyRain + cloudyDry))
console.log("P(rain | clear)  =", clearRain / (clearRain + clearDry))

// The same thing with the formula  P(A | B) = P(A and B) / P(B):
const pRainAndCloudy = cloudyRain / days
const pCloudy = (cloudyRain + cloudyDry) / days
console.log("by formula       =", pRainAndCloudy / pCloudy)

// It does NOT work backwards: P(cloudy | rain) is a different number.
console.log("P(cloudy | rain) =", cloudyRain / (cloudyRain + clearRain))
`,
}

export const bayesRule: ExerciseDef = {
  starter: `// 1. posterior(prior, sensitivity, falseAlarm): P(sick | positive test).
//    prior        = P(sick)
//    sensitivity  = P(positive | sick)
//    falseAlarm   = P(positive | healthy)
//    Bayes:  P(sick | +) = P(+ | sick)·P(sick) / P(+),   where
//            P(+) = P(+ | sick)·P(sick) + P(+ | healthy)·P(healthy)
function posterior(prior: number, sensitivity: number, falseAlarm: number): number {
  return 0
}

// 2. afterTests(prior, sensitivity, falseAlarm, k): the probability after k positive tests in a row.
//    Each test's answer becomes the next test's prior.
function afterTests(prior: number, sensitivity: number, falseAlarm: number, k: number): number {
  return prior
}

console.log(posterior(0.01, 0.9, 0.09))         // ≈ 0.092 — surprisingly low!
console.log(afterTests(0.01, 0.9, 0.09, 3))     // much more convincing
`,
  solution: `function posterior(prior: number, sensitivity: number, falseAlarm: number): number {
  const pPositive = sensitivity * prior + falseAlarm * (1 - prior)
  return (sensitivity * prior) / pPositive
}

function afterTests(prior: number, sensitivity: number, falseAlarm: number, k: number): number {
  let p = prior
  for (let i = 0; i < k; i++) p = posterior(p, sensitivity, falseAlarm)
  return p
}

console.log(posterior(0.01, 0.9, 0.09))
console.log(afterTests(0.01, 0.9, 0.09, 3))
`,
  tests: `
test("rare disease, decent test: only ≈ 9.2% of positives are sick", () => expect(posterior(0.01, 0.9, 0.09), "posterior(0.01, 0.9, 0.09)").toBeCloseTo(0.009 / (0.009 + 0.0891), 1e-9))
test("a perfect test (no false alarms) gives certainty", () => expect(posterior(0.01, 0.9, 0), "posterior(0.01, 0.9, 0)").toBeCloseTo(1))
test("a useless test (same rate either way) changes nothing", () => expect(posterior(0.2, 0.5, 0.5), "posterior(0.2, 0.5, 0.5)").toBeCloseTo(0.2))
test("0 tests leaves the prior alone", () => expect(afterTests(0.01, 0.9, 0.09, 0), "afterTests(…, 0)").toBeCloseTo(0.01))
test("three positive tests in a row → about 91%", () => expect(afterTests(0.01, 0.9, 0.09, 3), "afterTests(…, 3)").toBeBetween(0.9, 0.92))
`,
}

export const spamWord: ExerciseDef = {
  starter: `const emails = data.emails   // { text, spam }: spam is 1 or 0
const has = (text: string, word: string) => text.split(" ").includes(word)

// 1. pWordGiven(word, spam): the fraction of emails of that kind (spam = 1 or 0) that contain the word.
function pWordGiven(word: string, spam: number): number {
  return 0
}

// 2. pSpamGivenWord(word): Bayes' rule, using pWordGiven and the overall fraction of spam.
function pSpamGivenWord(word: string): number {
  return 0
}

for (const w of ["free", "click", "meeting", "you"]) {
  console.log(w.padEnd(8), "P(word | spam) =", round(pWordGiven(w, 1), 2), "  P(spam | word) =", round(pSpamGivenWord(w), 2))
}
`,
  solution: `const emails = data.emails
const has = (text: string, word: string) => text.split(" ").includes(word)

function pWordGiven(word: string, spam: number): number {
  const group = emails.filter(e => e.spam === spam)
  return group.filter(e => has(e.text, word)).length / group.length
}

function pSpamGivenWord(word: string): number {
  const pSpam = emails.filter(e => e.spam === 1).length / emails.length
  const top = pWordGiven(word, 1) * pSpam
  return top / (top + pWordGiven(word, 0) * (1 - pSpam))
}

for (const w of ["free", "click", "meeting", "you"]) {
  console.log(w.padEnd(8), "P(word | spam) =", round(pWordGiven(w, 1), 2), "  P(spam | word) =", round(pSpamGivenWord(w), 2))
}
`,
  tests: `
test("15 of the 36 spam emails contain 'free'", () => expect(pWordGiven("free", 1), "pWordGiven('free', 1)").toBeCloseTo(15 / 36))
test("only 1 of the 36 normal emails does", () => expect(pWordGiven("free", 0), "pWordGiven('free', 0)").toBeCloseTo(1 / 36))
test("an email containing 'free' is spam with probability 15/16", () => expect(pSpamGivenWord("free"), "pSpamGivenWord('free')").toBeCloseTo(15 / 16))
test("'meeting' never appears in spam → probability 0", () => expect(pSpamGivenWord("meeting"), "pSpamGivenWord('meeting')").toBeCloseTo(0))
`,
}

// ─── 5.4 Expected value ───────────────────────────────────────────────────────

export const expectation: ExerciseDef = {
  starter: `// 1. expectedValue(values, probs): Σ pᵢ·xᵢ — the probability-weighted average.
function expectedValue(values: number[], probs: number[]): number {
  return 0
}

// 2. variance(values, probs): the expected SQUARED distance from the expected value.
function variance(values: number[], probs: number[]): number {
  return 0
}

// 3. estimate(sample, n): the plain average of n results from a random function —
//    by the law of large numbers, this closes in on the expected value.
function estimate(sample: () => number, n: number): number {
  return 0
}

const die = [1, 2, 3, 4, 5, 6]
const fair = [1, 1, 1, 1, 1, 1].map(x => x / 6)
console.log(expectedValue(die, fair), variance(die, fair), estimate(() => random.int(1, 7), 10000))
`,
  solution: `function expectedValue(values: number[], probs: number[]): number {
  let total = 0
  for (let i = 0; i < values.length; i++) total += probs[i] * values[i]
  return total
}

function variance(values: number[], probs: number[]): number {
  const mu = expectedValue(values, probs)
  return expectedValue(values.map(x => (x - mu) ** 2), probs)
}

function estimate(sample: () => number, n: number): number {
  let total = 0
  for (let i = 0; i < n; i++) total += sample()
  return total / n
}

const die = [1, 2, 3, 4, 5, 6]
const fair = [1, 1, 1, 1, 1, 1].map(x => x / 6)
console.log(expectedValue(die, fair), variance(die, fair), estimate(() => random.int(1, 7), 10000))
`,
  tests: `
test("a fair die averages 3.5", () => expect(expectedValue([1, 2, 3, 4, 5, 6], new Array(6).fill(1 / 6)), "E[die]").toBeCloseTo(3.5))
test("a lottery ticket loses 1 per play on average", () => expect(expectedValue([98, -2], [0.01, 0.99]), "E[lottery]").toBeCloseTo(-1))
test("variance of a fair die is 35/12", () => expect(variance([1, 2, 3, 4, 5, 6], new Array(6).fill(1 / 6)), "Var[die]").toBeCloseTo(35 / 12))
test("a sure thing has variance 0", () => expect(variance([5, 9], [1, 0]), "variance").toBeCloseTo(0))
test("estimate closes in on 3.5 for a die", () => expect(estimate(() => random.int(1, 7), 20000), "estimate").toBeBetween(3.45, 3.55))
`,
}

export const decide: ExerciseDef = {
  starter: `// Decisions under uncertainty: pick the action with the best EXPECTED reward.
// rewards[action][outcome] — how good each action turns out in each possible world.
// Example: take an umbrella?   outcomes: [rain, no rain]
const umbrella = [
  [5, -1],    // take it:  dry if it rains, a little annoying if not
  [-10, 2],   // leave it: soaked if it rains, free hands if not
]

// bestAction(rewards, probs): the index of the action with the highest expected reward.
function bestAction(rewards: number[][], probs: number[]): number {
  return 0
}

for (const pRain of [0.05, 0.2, 0.5]) console.log("P(rain) =", pRain, "→ action", bestAction(umbrella, [pRain, 1 - pRain]))
`,
  solution: `const umbrella = [
  [5, -1],
  [-10, 2],
]

function bestAction(rewards: number[][], probs: number[]): number {
  const values = rewards.map(row => row.reduce((s, r, i) => s + r * probs[i], 0))
  return vec.argmax(values)
}

for (const pRain of [0.05, 0.2, 0.5]) console.log("P(rain) =", pRain, "→ action", bestAction(umbrella, [pRain, 1 - pRain]))
`,
  tests: `
test("5% chance of rain → leave the umbrella", () => expect(bestAction([[5, -1], [-10, 2]], [0.05, 0.95]), "action").toBe(1))
test("50% chance of rain → take it", () => expect(bestAction([[5, -1], [-10, 2]], [0.5, 0.5]), "action").toBe(0))
test("works with three actions and three outcomes", () => expect(bestAction([[1, 1, 1], [0, 5, 0], [3, 0, 0]], [0.2, 0.5, 0.3]), "action").toBe(1))
`,
}

// ─── 5.7 Maximum likelihood ───────────────────────────────────────────────────

export const coinLikelihood: ExerciseDef = {
  starter: `// 1. logLikelihood(p, flips): the log of the probability of seeing exactly these flips
//    from a coin with P(heads) = p. Each "H" contributes log(p); each "T" contributes log(1 − p).
function logLikelihood(p: number, flips: string): number {
  return 0
}

// 2. bestP(flips): try p = 0.001, 0.002, …, 0.999 and return the one with the highest log-likelihood.
function bestP(flips: string): number {
  return 0.5
}

const flips = "HHTHHHTHTH"
console.log(bestP(flips), "vs. heads/flips =", 7 / 10)
plot.fn(p => logLikelihood(p, flips), [0.01, 0.99], { title: "log-likelihood of the flips for every p" })
`,
  solution: `function logLikelihood(p: number, flips: string): number {
  let total = 0
  for (const f of flips) total += f === "H" ? Math.log(p) : Math.log(1 - p)
  return total
}

function bestP(flips: string): number {
  let best = 0.001
  for (let i = 1; i < 1000; i++) {
    const p = i / 1000
    if (logLikelihood(p, flips) > logLikelihood(best, flips)) best = p
  }
  return best
}

const flips = "HHTHHHTHTH"
console.log(bestP(flips), "vs. heads/flips =", 7 / 10)
plot.fn(p => logLikelihood(p, flips), [0.01, 0.99], { title: "log-likelihood of the flips for every p" })
`,
  tests: `
test("logLikelihood(0.5, 'HT') = 2·log(0.5)", () => expect(logLikelihood(0.5, "HT"), "logLikelihood(0.5, 'HT')").toBeCloseTo(2 * Math.log(0.5)))
test("logLikelihood(0.9, 'HHT') = 2·log(0.9) + log(0.1)", () => expect(logLikelihood(0.9, "HHT"), "logLikelihood").toBeCloseTo(2 * Math.log(0.9) + Math.log(0.1)))
test("the best p for 7 heads in 10 is 0.7", () => expect(bestP("HHTHHHTHTH"), "bestP").toBeCloseTo(0.7))
test("the best p for 1 head in 4 is 0.25", () => expect(bestP("TTHT"), "bestP('TTHT')").toBeCloseTo(0.25))
`,
}

export const gaussianMLE: ExerciseDef = {
  starter: `// Measurements with bell-curve noise. Which centre μ makes them most likely?
const xs = [4.8, 5.3, 5.1, 4.6, 5.9, 5.2, 4.9]

// The bell curve's log-density at x (spread σ):  −(x − μ)²/(2σ²) − log(σ·√(2π))
// 1. logLik(mu, sigma, xs): the sum of the log-densities of all the measurements.
function logLik(mu: number, sigma: number, xs: number[]): number {
  return 0
}

// 2. bestMu(xs): try μ from 0 to 10 in steps of 0.001 (with σ = 1); return the most likely.
function bestMu(xs: number[]): number {
  return 0
}

console.log("most likely μ:", bestMu(xs), "  plain average:", vec.mean(xs))
`,
  solution: `const xs = [4.8, 5.3, 5.1, 4.6, 5.9, 5.2, 4.9]

function logLik(mu: number, sigma: number, xs: number[]): number {
  let total = 0
  for (const x of xs) total += -((x - mu) ** 2) / (2 * sigma * sigma) - Math.log(sigma * Math.sqrt(2 * Math.PI))
  return total
}

function bestMu(xs: number[]): number {
  let best = 0
  for (let i = 0; i <= 10000; i++) {
    const mu = i / 1000
    if (logLik(mu, 1, xs) > logLik(best, 1, xs)) best = mu
  }
  return best
}

console.log("most likely μ:", bestMu(xs), "  plain average:", vec.mean(xs))
`,
  tests: `
test("logLik of one point at the centre (σ = 1) is −log(√(2π))", () => expect(logLik(3, 1, [3]), "logLik(3, 1, [3])").toBeCloseTo(-Math.log(Math.sqrt(2 * Math.PI))))
test("points further from μ are less likely", () => expect(logLik(0, 1, [2]) < logLik(0, 1, [1]), "logLik(0,1,[2]) < logLik(0,1,[1])").toBe(true))
test("the most likely μ is the average", () => expect(bestMu([4.8, 5.3, 5.1, 4.6, 5.9, 5.2, 4.9]), "bestMu").toBeCloseTo(vec.mean([4.8, 5.3, 5.1, 4.6, 5.9, 5.2, 4.9]), 1e-3))
test("…for any data", () => expect(bestMu([1, 2, 9]), "bestMu([1, 2, 9])").toBeCloseTo(4, 1e-3))
`,
}

export const categoricalMLE: ExerciseDef = {
  starter: `// A model for single letters: one probability per letter. What's the most likely model for some text?
const text = "abracadabra"

// 1. fit(text): the maximum-likelihood probabilities — just count and divide! Returns a Map.
function fit(text: string): Map<string, number> {
  return new Map()
}

// 2. nll(model, text): the AVERAGE negative log-likelihood of the text under the model:
//    −(1/n) Σ log P(letter).  (Look familiar? It's cross-entropy.)
function nll(model: Map<string, number>, text: string): number {
  return 0
}

const model = fit(text)
console.log(model, nll(model, text))
// Any other model does worse on this text:
const uniform = new Map([..."abcdr"].map(c => [c, 1 / 5]))
console.log("uniform model:", nll(uniform, text))
`,
  solution: `const text = "abracadabra"

function fit(text: string): Map<string, number> {
  const counts = new Map<string, number>()
  for (const c of text) counts.set(c, (counts.get(c) ?? 0) + 1)
  const model = new Map<string, number>()
  for (const [c, k] of counts) model.set(c, k / text.length)
  return model
}

function nll(model: Map<string, number>, text: string): number {
  let total = 0
  for (const c of text) total -= Math.log(model.get(c) ?? 0)
  return total / text.length
}

const model = fit(text)
console.log(model, nll(model, text))
const uniform = new Map([..."abcdr"].map(c => [c, 1 / 5]))
console.log("uniform model:", nll(uniform, text))
`,
  tests: `
test("P(a) in 'abracadabra' is 5/11", () => expect(fit("abracadabra").get("a"), "fit(…).get('a')").toBeCloseTo(5 / 11))
test("the probabilities add up to 1", () => expect([...fit("hello world").values()].reduce((a, b) => a + b, 0), "sum").toBeCloseTo(1))
test("nll of 'ab' under {a: ½, b: ½} is log 2", () => expect(nll(new Map([["a", 0.5], ["b", 0.5]]), "ab"), "nll").toBeCloseTo(Math.log(2)))
test("the fitted model beats the uniform one", () => {
  const __t = "abracadabra"
  expect(nll(fit(__t), __t) < nll(new Map([..."abcdr"].map(c => [c, 0.2])), __t), "fitted < uniform").toBe(true)
})
test("…and beats a tweaked version of itself", () => {
  const __t = "abracadabra"
  const __m = fit(__t)
  const __tweak = new Map(__m)
  __tweak.set("a", __m.get("a") - 0.05)
  __tweak.set("b", __m.get("b") + 0.05)
  expect(nll(__m, __t) < nll(__tweak, __t), "fitted < tweaked").toBe(true)
})
`,
}

// ─── 5.8 Entropy & KL divergence ──────────────────────────────────────────────

export const entropyKL: ExerciseDef = {
  starter: `// All in bits (log base 2). Skip any term where p is 0 — it contributes nothing.

// 1. entropy(p) = −Σ pᵢ·log₂(pᵢ): the average surprise.
function entropy(p: number[]): number {
  return 0
}

// 2. crossEntropy(p, q) = −Σ pᵢ·log₂(qᵢ): the average surprise if the truth is p but you expect q.
function crossEntropy(p: number[], q: number[]): number {
  return 0
}

// 3. kl(p, q) = crossEntropy(p, q) − entropy(p): the EXTRA surprise from using the wrong distribution.
function kl(p: number[], q: number[]): number {
  return 0
}

console.log(entropy([0.5, 0.5]), entropy([1, 0]), entropy([0.25, 0.25, 0.25, 0.25]))
console.log(kl([0.9, 0.1], [0.5, 0.5]), kl([0.5, 0.5], [0.9, 0.1]))
`,
  solution: `function entropy(p: number[]): number {
  let total = 0
  for (const x of p) if (x > 0) total -= x * Math.log2(x)
  return total
}

function crossEntropy(p: number[], q: number[]): number {
  let total = 0
  for (let i = 0; i < p.length; i++) if (p[i] > 0) total -= p[i] * Math.log2(q[i])
  return total
}

function kl(p: number[], q: number[]): number {
  return crossEntropy(p, q) - entropy(p)
}

console.log(entropy([0.5, 0.5]), entropy([1, 0]), entropy([0.25, 0.25, 0.25, 0.25]))
console.log(kl([0.9, 0.1], [0.5, 0.5]), kl([0.5, 0.5], [0.9, 0.1]))
`,
  tests: `
test("a fair coin has 1 bit of entropy", () => expect(entropy([0.5, 0.5]), "entropy([0.5, 0.5])").toBeCloseTo(1))
test("a sure thing has 0", () => expect(entropy([1, 0]), "entropy([1, 0])").toBeCloseTo(0))
test("4 equal options: 2 bits", () => expect(entropy([0.25, 0.25, 0.25, 0.25]), "entropy").toBeCloseTo(2))
test("cross-entropy with yourself is the entropy", () => expect(crossEntropy([0.7, 0.2, 0.1], [0.7, 0.2, 0.1]), "H(p, p)").toBeCloseTo(entropy([0.7, 0.2, 0.1])))
test("KL of a distribution with itself is 0", () => expect(kl([0.3, 0.7], [0.3, 0.7]), "kl(p, p)").toBeCloseTo(0))
test("KL is never negative", () => expect(kl([0.2, 0.5, 0.3], [0.6, 0.2, 0.2]), "kl").toBeGreaterThan(0))
test("KL is not symmetric", () => expect(Math.abs(kl([0.9, 0.1], [0.5, 0.5]) - kl([0.5, 0.5], [0.9, 0.1])), "|KL(p‖q) − KL(q‖p)|").toBeGreaterThan(0.1))
`,
}

export const charEntropy: ExerciseDef = {
  starter: `// How many bits does each character of English really carry?
// bitsPerChar(text): count every character, turn counts into probabilities, return their entropy in bits.
function bitsPerChar(text: string): number {
  return 0
}

const stories = data.stories
console.log("characters used:", new Set(stories).size)
console.log("if every character were equally likely:", round(Math.log2(new Set(stories).size), 3), "bits")
console.log("using real letter frequencies:         ", round(bitsPerChar(stories), 3), "bits")
`,
  solution: `function bitsPerChar(text: string): number {
  const counts = new Map<string, number>()
  for (const c of text) counts.set(c, (counts.get(c) ?? 0) + 1)
  let h = 0
  for (const k of counts.values()) {
    const p = k / text.length
    h -= p * Math.log2(p)
  }
  return h
}

const stories = data.stories
console.log("characters used:", new Set(stories).size)
console.log("if every character were equally likely:", round(Math.log2(new Set(stories).size), 3), "bits")
console.log("using real letter frequencies:         ", round(bitsPerChar(stories), 3), "bits")
`,
  tests: `
test("'aaaa' carries 0 bits per character", () => expect(bitsPerChar("aaaa"), "bitsPerChar('aaaa')").toBeCloseTo(0))
test("'abab' carries 1 bit per character", () => expect(bitsPerChar("abab"), "bitsPerChar('abab')").toBeCloseTo(1))
test("'abcd' carries 2", () => expect(bitsPerChar("abcd"), "bitsPerChar('abcd')").toBeCloseTo(2))
test("the stories need fewer bits than uniform", () => expect(bitsPerChar(data.stories) < Math.log2(new Set(data.stories).size), "real < uniform").toBe(true))
`,
}

// ─── 5.9 Project: a spam filter ───────────────────────────────────────────────

const NB_WORDS = `function words(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z]+/g, " ").split(" ").filter(w => w !== "")
}`

const NB_TRAIN_SOLUTION = `type Model = {
  spamCounts: Map<string, number>
  hamCounts: Map<string, number>
  spamWords: number
  hamWords: number
  vocab: Set<string>
  pSpam: number
}

function train(emails: { text: string; spam: number }[]): Model {
  const model: Model = { spamCounts: new Map(), hamCounts: new Map(), spamWords: 0, hamWords: 0, vocab: new Set(), pSpam: 0 }
  let spamEmails = 0
  for (const e of emails) {
    if (e.spam) spamEmails++
    const counts = e.spam ? model.spamCounts : model.hamCounts
    for (const w of words(e.text)) {
      counts.set(w, (counts.get(w) ?? 0) + 1)
      model.vocab.add(w)
      if (e.spam) model.spamWords++
      else model.hamWords++
    }
  }
  model.pSpam = spamEmails / emails.length
  return model
}`

const NB_SCORE_SOLUTION = `function pWord(model: Model, w: string, spam: boolean): number {
  const count = (spam ? model.spamCounts : model.hamCounts).get(w) ?? 0
  const total = spam ? model.spamWords : model.hamWords
  return (count + 1) / (total + model.vocab.size)
}

function score(model: Model, text: string): number {
  let s = Math.log(model.pSpam) - Math.log(1 - model.pSpam)
  for (const w of words(text)) s += Math.log(pWord(model, w, true)) - Math.log(pWord(model, w, false))
  return s
}

function isSpam(model: Model, text: string): boolean {
  return score(model, text) > 0
}`

export const nbTrain: ExerciseDef = {
  starter: `const emails = data.emails
${NB_WORDS}

type Model = {
  spamCounts: Map<string, number>   // how often each word appears across all spam
  hamCounts: Map<string, number>    // …and across all normal ("ham") email
  spamWords: number                 // total words in spam
  hamWords: number                  // total words in ham
  vocab: Set<string>                // every distinct word seen
  pSpam: number                     // fraction of emails that are spam
}

// train(emails): fill in every field of the model.
function train(emails: { text: string; spam: number }[]): Model {
  const model: Model = { spamCounts: new Map(), hamCounts: new Map(), spamWords: 0, hamWords: 0, vocab: new Set(), pSpam: 0 }
  return model
}

const model = train(emails.slice(0, 48))
console.log("vocabulary:", model.vocab.size, "words.  P(spam) =", model.pSpam)
console.log("'free' in spam:", model.spamCounts.get("free"), "  in ham:", model.hamCounts.get("free"))
`,
  solution: `const emails = data.emails
${NB_WORDS}

${NB_TRAIN_SOLUTION}

const model = train(emails.slice(0, 48))
console.log("vocabulary:", model.vocab.size, "words.  P(spam) =", model.pSpam)
console.log("'free' in spam:", model.spamCounts.get("free"), "  in ham:", model.hamCounts.get("free"))
`,
  tests: `
const __m = train([{ text: "win money now", spam: 1 }, { text: "lunch now", spam: 0 }, { text: "money money", spam: 1 }])
test("counts words per class", () => expect(__m.spamCounts.get("money"), "spamCounts money").toBe(3))
test("ham counts too", () => expect(__m.hamCounts.get("now"), "hamCounts now").toBe(1))
test("total words per class", () => expect([__m.spamWords, __m.hamWords], "[spamWords, hamWords]").toEqual([5, 2]))
test("vocabulary of distinct words", () => expect(__m.vocab.size, "vocab size").toBe(4))
test("P(spam) = 2/3", () => expect(__m.pSpam, "pSpam").toBeCloseTo(2 / 3))
`,
}

export const nbClassify: ExerciseDef = {
  starter: `const emails = data.emails
${NB_WORDS}

// (from Part 1)
${NB_TRAIN_SOLUTION}

// 1. pWord(model, w, spam): P(word | class) with "add-one smoothing" — so a word never seen
//    in a class doesn't get probability 0 (which would wipe out everything else):
//      (count of w in that class + 1) / (total words in that class + vocabulary size)
function pWord(model: Model, w: string, spam: boolean): number {
  return 0
}

// 2. score(model, text): log P(spam | words) − log P(ham | words).
//    "Naive" Bayes pretends words are independent, so the probabilities multiply — and in logs they ADD:
//      log P(spam) − log P(ham)  +  Σ over words [ log pWord(w, spam) − log pWord(w, ham) ]
function score(model: Model, text: string): number {
  return 0
}

// 3. isSpam: positive score → spam.
function isSpam(model: Model, text: string): boolean {
  return false
}

const model = train(emails.slice(0, 48))
for (const t of ["claim your free prize now", "see you at the team meeting", "are you free for lunch"]) {
  console.log(round(score(model, t), 2), isSpam(model, t) ? "SPAM" : "ok  ", t)
}
`,
  solution: `const emails = data.emails
${NB_WORDS}

${NB_TRAIN_SOLUTION}

${NB_SCORE_SOLUTION}

const model = train(emails.slice(0, 48))
for (const t of ["claim your free prize now", "see you at the team meeting", "are you free for lunch"]) {
  console.log(round(score(model, t), 2), isSpam(model, t) ? "SPAM" : "ok  ", t)
}
`,
  tests: `
const __m = train([{ text: "win money now", spam: 1 }, { text: "lunch now", spam: 0 }, { text: "money money", spam: 1 }])
test("pWord with smoothing: P(money | spam) = (3 + 1) / (5 + 4)", () => expect(pWord(__m, "money", true), "pWord(money, spam)").toBeCloseTo(4 / 9))
test("an unseen word still gets a small probability", () => expect(pWord(__m, "zebra", false), "pWord(zebra, ham)").toBeCloseTo(1 / 6))
test("score of empty text is just the prior's log-odds", () => expect(score(__m, ""), "score('')").toBeCloseTo(Math.log(2)))
test("obvious spam scores positive", () => expect(score(train(data.emails), "claim your free prize now") > 0, "score > 0").toBe(true))
test("a meeting email scores negative", () => expect(isSpam(train(data.emails), "notes from the project meeting"), "isSpam").toBe(false))
`,
}

export const nbEvaluate: ExerciseDef = {
  starter: `const emails = data.emails
${NB_WORDS}

// (from Parts 1 and 2)
${NB_TRAIN_SOLUTION}

${NB_SCORE_SOLUTION}

// Train on the first 48 emails; test on the 24 the model has never seen.
const trainSet = emails.slice(0, 48)
const testSet = emails.slice(48)
const model = train(trainSet)

// 1. accuracy(model, set): the fraction of emails it labels correctly.
function accuracy(model: Model, set: { text: string; spam: number }[]): number {
  return 0
}

// 2. spammiest(model, k): the k words in the vocabulary with the highest
//    log(pWord(w, spam)) − log(pWord(w, ham)) — the strongest evidence for spam.
function spammiest(model: Model, k: number): string[] {
  return []
}

console.log("train accuracy:", accuracy(model, trainSet))
console.log("test accuracy: ", accuracy(model, testSet))
console.log("spammiest words:", spammiest(model, 8))
for (const e of testSet) if (isSpam(model, e.text) !== (e.spam === 1)) console.log("MISTAKE:", e.spam ? "spam" : "ham", "—", e.text)
`,
  solution: `const emails = data.emails
${NB_WORDS}

${NB_TRAIN_SOLUTION}

${NB_SCORE_SOLUTION}

const trainSet = emails.slice(0, 48)
const testSet = emails.slice(48)
const model = train(trainSet)

function accuracy(model: Model, set: { text: string; spam: number }[]): number {
  return set.filter(e => isSpam(model, e.text) === (e.spam === 1)).length / set.length
}

function spammiest(model: Model, k: number): string[] {
  const evidence = (w: string) => Math.log(pWord(model, w, true)) - Math.log(pWord(model, w, false))
  return [...model.vocab].sort((a, b) => evidence(b) - evidence(a)).slice(0, k)
}

console.log("train accuracy:", accuracy(model, trainSet))
console.log("test accuracy: ", accuracy(model, testSet))
console.log("spammiest words:", spammiest(model, 8))
for (const e of testSet) if (isSpam(model, e.text) !== (e.spam === 1)) console.log("MISTAKE:", e.spam ? "spam" : "ham", "—", e.text)
`,
  tests: `
test("it gets at least 90% of the unseen emails right", () => expect(accuracy(train(data.emails.slice(0, 48)), data.emails.slice(48)), "test accuracy").toBeGreaterThan(0.9))
test("accuracy of an always-wrong set is 0", () => expect(accuracy(train(data.emails), [{ text: "claim your free prize now", spam: 0 }]), "accuracy").toBe(0))
test("spammiest returns k words", () => expect(spammiest(train(data.emails), 5), "spammiest(…, 5)").toHaveLength(5))
test("'now' is among the 10 spammiest words", () => expect(spammiest(train(data.emails), 10).includes("now"), "includes 'now'").toBe(true))
`,
}
